/**
 * product-3d-placement.js — configurator placement layer for the PDP 3D viewer.
 *
 * Owns the "+" pins, the hint bar and the slot popup. Deliberately knows nothing about three.js,
 * model-viewer or GLBs: the renderer projects slot positions and calls setPositions() each frame,
 * and this module turns that into DOM. That keeps the whole interaction reviewable and testable
 * before the renderer exists, and keeps the renderer swappable afterwards.
 *
 * Two ways to place an accessory, per Figma 73-37863 and 24-14319:
 *   A. Add an accessory through the existing PDP popup -> eligible slots turn blue -> click one.
 *   B. Click a "+" pin -> pick an accessory -> configure it in the existing PDP popup.
 * Both paths end in the same place() call, so the two surfaces can never disagree.
 *
 * Public API, per viewer root:
 *   const p = BranchPlacement.mount(rootEl)
 *   p.setSlots([{ id, label, kind }])         // from the product profile
 *   p.setCatalog({ slotId: [choice, ...] }) // choice: { handle, variantId, name, image, size }
 *   p.setPositions({ slotId: { x, y, visible } })   // called by the renderer each frame
 *   p.setPlaced({ slotId: choice })           // reflect current configuration
 *   p.beginPlacement(handle) / p.cancelPlacement() / p.reset()
 * Events on the root: `branch:3d-placement:place` { slotId, choice }, `:remove` { slotId },
 *   `:mode` { mode }.
 */
(function () {
  'use strict';

  var MODE_IDLE = 'idle';
  var MODE_PLACING = 'placing';

  function emit(root, name, detail) {
    root.dispatchEvent(new CustomEvent('branch:3d-placement:' + name, { detail: detail, bubbles: true }));
  }

  function Placement(root) {
    this.root = root;
    this.pinsEl = root.querySelector('[data-product-3d-pins]');
    this.pinTemplate = root.querySelector('[data-product-3d-pin-template]');
    this.popupEl = root.querySelector('[data-product-3d-slot-popup]');
    this.choicesEl = root.querySelector('[data-product-3d-slot-choices]');
    this.choiceTemplate = root.querySelector('[data-product-3d-choice-template]');
    this.emptyEl = root.querySelector('[data-product-3d-slot-empty]');
    this.removeBtn = root.querySelector('[data-product-3d-slot-remove]');
    this.titleEl = root.querySelector('[data-product-3d-slot-title]');
    this.hintBar = root.querySelector('[data-product-3d-hint-bar]');
    this.hintIdle = root.querySelector('[data-product-3d-hint-idle]');
    this.hintPlacing = root.querySelector('[data-product-3d-hint-placing]');
    this.resetBtn = root.querySelector('[data-product-3d-reset]');

    this.slots = [];
    this.catalog = {};
    this.placed = {};
    this.pins = new Map();
    this.positions = {};
    this.hasPlacedAddon = false;
    this.mode = MODE_IDLE;
    this.placingHandle = null;
    this.placingVariantId = null;
    this.openSlotId = null;

    this._bind();
  }

  Placement.prototype._bind = function () {
    var self = this;

    if (this.pinsEl) {
      this.pinsEl.addEventListener('click', function (event) {
        var pin = event.target.closest('[data-product-3d-pin]');
        if (!pin || !self.pinsEl.contains(pin)) return;
        event.preventDefault();
        var slotId = pin.dataset.slot;
        if (self.mode === MODE_PLACING) self._placeFromRail(slotId);
        else if (self.openSlotId === slotId) self.closeSlot();
        else self.openSlot(slotId);
      });
    }

    if (this.popupEl) {
      this.popupEl.addEventListener('click', function (event) {
        if (event.target.closest('[data-product-3d-slot-remove]')) { event.preventDefault(); self.remove(self.openSlotId); return; }
        if (event.target.closest('[data-product-3d-slot-popup-close]')) { event.preventDefault(); self.closeSlot(); return; }
        var choice = event.target.closest('[data-product-3d-choice]');
        if (!choice || choice.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        var index = Number(choice.dataset.choiceIndex);
        var list = self.displayChoices || [];
        var picked = list[index];
        if (picked) emit(self.root, 'choose', { slotId: self.openSlotId, choice: picked });
      });
    }

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', function (event) { event.preventDefault(); self.reset(); });
    }

    this._onKeydown = function (event) {
      if (event.key !== 'Escape' || !self.isActive()) return;
      var dialog = event.target && event.target.closest && event.target.closest('[role="dialog"], .tingle-modal');
      if (dialog && dialog !== self.popupEl && !dialog.contains(self.root)) return;
      if (self.openSlotId) { self.closeSlot(); event.stopImmediatePropagation(); }
      else if (self.mode === MODE_PLACING) { self.cancelPlacement(); event.stopImmediatePropagation(); }
    };
    document.addEventListener('keydown', this._onKeydown, true);

    this._onDocClick = function (event) {
      if (!self.openSlotId) return;
      if (event.target.closest('[data-product-3d-slot-popup]') || event.target.closest('[data-product-3d-pin]')) return;
      self.closeSlot(false);
    };
    document.addEventListener('click', this._onDocClick);

    // PDP rail clicks deliberately stay with the theme's original quick-view popup.
    // Its completed Add action starts placement through the controller.

  };

  Placement.prototype.destroy = function () {
    document.removeEventListener('keydown', this._onKeydown, true);
    document.removeEventListener('click', this._onDocClick);
  };

  /** The configurator is active when the viewer is open and at least one slot is known. */
  Placement.prototype.isActive = function () {
    return !!(this.pinsEl && !this.pinsEl.hidden && this.slots.length);
  };

  Placement.prototype.setSlots = function (slots) {
    this.slots = Array.isArray(slots) ? slots.slice() : [];
    this._renderPins();
    return this;
  };

  Placement.prototype.setCatalog = function (catalog) {
    this.catalog = catalog || {};
    if (this.openSlotId) this._renderChoices(this.openSlotId, this.filterHandle);
    this._syncPinStates();
    return this;
  };

  Placement.prototype.setPlaced = function (placed) {
    this.placed = placed || {};
    if (Object.keys(this.placed).length) this.hasPlacedAddon = true;
    if (this.resetBtn) this.resetBtn.hidden = !Object.keys(this.placed).length;
    this._syncHints();
    this._syncPinStates();
    return this;
  };

  /* A pin is 28px with a 44px tap area (its ::after reaches 8px out). Pins whose points project
     closer than this would cover each other's centre: on a phone the Tria's rear insert, In-Desk
     Power and Cable Tray pins line up in one column at some angles, and a tap on one opened another. */
  var PIN_SPACING = 36;

  /** Push apart shown pins closer than PIN_SPACING along the line between them (straight down for
      the same point), so each pin's centre stays its own tap target. Anchors are not changed. */
  function spreadPins(points) {
    for (var pass = 0; pass < 6; pass++) {
      var moved = false;
      for (var i = 0; i < points.length; i++) {
        for (var j = i + 1; j < points.length; j++) {
          var a = points[i], b = points[j];
          var dx = b.x - a.x, dy = b.y - a.y, d = Math.sqrt(dx * dx + dy * dy);
          if (d >= PIN_SPACING) continue;
          if (d < 0.5) { dx = 0; dy = 1; d = 1; }
          var push = (PIN_SPACING - d) / 2 + 0.01;
          a.x -= dx / d * push; a.y -= dy / d * push;
          b.x += dx / d * push; b.y += dy / d * push;
          moved = true;
        }
      }
      if (!moved) return;
    }
  }

  /** Called by the renderer every frame with projected screen positions. */
  Placement.prototype.setPositions = function (positions) {
    if (!positions) return this;
    this.positions = positions;
    var self = this;
    var shown = [];
    this.pins.forEach(function (pin, slotId) {
      var p = positions[slotId];
      var slot = self.slots.find(function (candidate) { return candidate.id === slotId; });
      var eligible = slot && !self.placed[slotId] && self._slotAccepts(slot, self.placingHandle);
      if (!p || p.visible === false || (self.mode === MODE_PLACING && !eligible) ||
          (!self.placed[slotId] && !self._displayChoices(slotId).length)) { pin.hidden = true; return; }
      pin.hidden = false;
      shown.push({ pin: pin, x: p.x, y: p.y });
    });
    spreadPins(shown);
    shown.forEach(function (s) {
      // -50% keeps the 28px pin centred on its point.
      s.pin.style.transform = 'translate(' + Math.round(s.x) + 'px,' + Math.round(s.y) + 'px) translate(-50%,-50%)';
    });
    if (this.openSlotId) this._positionPopup(this.openSlotId);
    return this;
  };

  Placement.prototype.show = function () {
    if (this.pinsEl) this.pinsEl.hidden = false;
    this._syncHints();
    if (this.resetBtn) this.resetBtn.hidden = !Object.keys(this.placed).length && this.mode !== MODE_PLACING;
    return this;
  };

  Placement.prototype._syncHints = function () {
    var placing = this.mode === MODE_PLACING;
    if (this.hintIdle) this.hintIdle.hidden = placing || this.hasPlacedAddon;
    if (this.hintPlacing) this.hintPlacing.hidden = !placing;
    if (this.hintBar) this.hintBar.hidden = !this.isActive() || (!placing && this.hasPlacedAddon);
  };
  Placement.prototype.hide = function () {
    this.closeSlot(false);
    this.cancelPlacement(true);
    if (this.pinsEl) this.pinsEl.hidden = true;
    if (this.hintBar) this.hintBar.hidden = true;
    if (this.resetBtn) this.resetBtn.hidden = true;
    return this;
  };

  Placement.prototype._renderPins = function () {
    if (!this.pinsEl || !this.pinTemplate) return;
    this.pinsEl.textContent = '';
    this.pins.clear();
    var frag = document.createDocumentFragment();
    for (var i = 0; i < this.slots.length; i++) {
      var slot = this.slots[i];
      var node = this.pinTemplate.content.firstElementChild.cloneNode(true);
      node.dataset.slot = slot.id;
      node.dataset.kind = slot.kind || '';
      node.hidden = true; // until the renderer reports a position
      var label = node.querySelector('[data-product-3d-pin-label]');
      if (label) label.textContent = slot.label || slot.id;
      node.setAttribute('aria-label', this._pinLabel(slot, null));
      frag.appendChild(node);
      this.pins.set(slot.id, node);
    }
    this.pinsEl.appendChild(frag);
    this._syncPinStates();
  };

  Placement.prototype._pinLabel = function (slot, placedChoice) {
    var where = slot.label || slot.id;
    return placedChoice ? 'Edit ' + this._choiceName(placedChoice) + ' at ' + where : 'Add an accessory at ' + where;
  };

  Placement.prototype._syncPinStates = function () {
    var self = this;
    this.slots.forEach(function (slot) {
      var pin = self.pins.get(slot.id);
      if (!pin) return;
      var placedChoice = self.placed[slot.id] || null;
      var state = placedChoice ? 'filled' : 'idle';
      if (self.mode === MODE_PLACING && !placedChoice && self._slotAccepts(slot, self.placingHandle)) state = 'eligible';
      pin.dataset.state = state;
      pin.setAttribute('aria-label', self._pinLabel(slot, placedChoice));
      pin.setAttribute('aria-expanded', String(self.openSlotId === slot.id));
      var label = pin.querySelector('[data-product-3d-pin-label]');
      if (label) label.textContent = placedChoice ? 'Edit ' + self._choiceName(placedChoice) : '';
    });
    this.setPositions(this.positions);
  };

  Placement.prototype._choicesForSlot = function (slotId) {
    var slot = this.slots.find(function (s) { return s.id === slotId; });
    if (!slot) return [];
    return this.catalog[slot.id] || this.catalog[slot.kind] || [];
  };

  Placement.prototype._slotAccepts = function (slot, handle) {
    if (!handle) return false;
    var list = this._choicesForSlot(slot.id);
    var variantId = this.placingVariantId;
    return list.some(function (c) { return c.handle === handle && !c.unavailable && (!variantId || String(c.variantId) === String(variantId)); });
  };

  /** True when any slot on this product could take the add-on. Gates the rail interception. */
  Placement.prototype.canPlace = function (handle) {
    var self = this;
    return this.slots.some(function (slot) { return self._slotAccepts(slot, handle); });
  };

  Placement.prototype.beginPlacement = function (handle, variantId) {
    if (this.mode === MODE_PLACING) this._markRail(this.placingHandle, false);
    this.closeSlot();
    this.mode = MODE_PLACING;
    this.placingHandle = handle;
    this.placingVariantId = variantId || null;
    if (this.pinsEl) this.pinsEl.dataset.mode = MODE_PLACING;
    this._syncHints();
    if (this.resetBtn) this.resetBtn.hidden = false;
    this._markRail(handle, true);
    this._syncPinStates();
    emit(this.root, 'mode', { mode: MODE_PLACING, handle: handle });
    return this;
  };

  Placement.prototype.cancelPlacement = function (silent) {
    if (this.mode === MODE_IDLE) return this;
    this._markRail(this.placingHandle, false);
    this.mode = MODE_IDLE;
    this.placingHandle = null;
    this.placingVariantId = null;
    if (this.pinsEl) this.pinsEl.dataset.mode = MODE_IDLE;
    this._syncHints();
    if (this.resetBtn) this.resetBtn.hidden = !Object.keys(this.placed).length;
    this._syncPinStates();
    emit(this.root, 'mode', { mode: MODE_IDLE });
    if (!silent) emit(this.root, 'cancel', {});
    return this;
  };

  Placement.prototype._markRail = function (handle, on) {
    if (!handle) return;
    var tiles = document.querySelectorAll('.addons__container [data-addon-handle="' + CSS.escape(handle) + '"]');
    for (var i = 0; i < tiles.length; i++) {
      if (on) tiles[i].setAttribute('data-product-3d-placing', 'true');
      else tiles[i].removeAttribute('data-product-3d-placing');
    }
  };

  Placement.prototype._placeFromRail = function (slotId) {
    var slot = this.slots.find(function (s) { return s.id === slotId; });
    if (!slot || this.placed[slotId] || !this._slotAccepts(slot, this.placingHandle)) return this;
    var handle = this.placingHandle;
    var variantId = this.placingVariantId;
    var list = this._choicesForSlot(slotId).filter(function (c) { return c.handle === handle && !c.unavailable && (!variantId || String(c.variantId) === String(variantId)); });
    // Several variants of the same add-on can fit one slot (small vs large tray). Ask rather than guess.
    if (list.length > 1) { this.cancelPlacement(); return this.openSlot(slotId, handle); }
    if (list.length === 1) this.place(slotId, list[0]);
    return this;
  };

  Placement.prototype.openSlot = function (slotId, filterHandle) {
    if (!this.isActive()) return this;
    var list = this._displayChoices(slotId, filterHandle);
    var occupied = !!this.placed[slotId];
    var singleProduct = !list.length || list.every(function (choice) { return choice.handle === list[0].handle; });
    if (!occupied && singleProduct) {
      this.closeSlot(false);
      if (list.length) emit(this.root, 'choose', { slotId: slotId, choice: list[0] });
      return this;
    }
    this.openSlotId = slotId;
    this.filterHandle = filterHandle || null;
    if (this.removeBtn) this.removeBtn.hidden = !occupied;
    if (this.titleEl) this.titleEl.textContent = occupied ? this.titleEl.dataset.filledTitle : this.titleEl.dataset.emptyTitle;
    this._renderChoices(slotId, filterHandle);
    if (this.popupEl) this.popupEl.hidden = false;
    this._positionPopup(slotId);
    this._syncPinStates();
    var first = occupied && this.removeBtn || this.popupEl && this.popupEl.querySelector('[data-product-3d-choice]');
    if (first) requestAnimationFrame(function () { first.focus({ preventScroll: true }); });
    return this;
  };

  Placement.prototype.closeSlot = function (restoreFocus) {
    if (!this.openSlotId) return this;
    var pin = this.pins.get(this.openSlotId);
    this.openSlotId = null;
    this.filterHandle = null;
    if (this.popupEl) this.popupEl.hidden = true;
    this._syncPinStates();
    if (restoreFocus !== false && pin && !pin.hidden) pin.focus({ preventScroll: true });
    return this;
  };

  Placement.prototype._choiceName = function (choice) {
    var name = choice.productName || choice.name || choice.handle || 'accessory';
    return choice.size && choice.size !== 'Standard' && name.toLowerCase().indexOf(choice.size.toLowerCase()) === -1 ? choice.size + ' ' + name : name;
  };

  Placement.prototype._displayChoices = function (slotId, filterHandle) {
    var all = this._choicesForSlot(slotId);
    var byKind = {};
    all.forEach(function (choice) {
      if (choice.unavailable || (filterHandle && choice.handle !== filterHandle)) return;
      var key = choice.handle + '|' + (choice.size || 'Standard');
      var existing = byKind[key];
      if (!existing || (choice.preferred && !existing.preferred)) byKind[key] = choice;
    });
    return Object.keys(byKind).map(function (key) { return byKind[key]; });
  };

  Placement.prototype._renderChoices = function (slotId, filterHandle) {
    if (!this.choicesEl || !this.choiceTemplate) return;
    var list = this._displayChoices(slotId, filterHandle);
    this.displayChoices = list;
    this.choicesEl.textContent = '';
    if (this.emptyEl) this.emptyEl.hidden = list.length > 0 || !!this.placed[slotId];
    var frag = document.createDocumentFragment();
    if (this.removeBtn && this.placed[slotId]) { this.removeBtn.hidden = false; frag.appendChild(this.removeBtn); }
    for (var i = 0; i < list.length; i++) {
      var choice = list[i];
      var node = this.choiceTemplate.content.firstElementChild.cloneNode(true);
      node.dataset.choiceIndex = String(i);
      node.dataset.addonHandle = choice.handle;
      var img = node.querySelector('[data-product-3d-choice-image]');
      if (img) { if (choice.image) { img.src = choice.image; img.alt = ''; } else { img.remove(); } }
      var name = node.querySelector('[data-product-3d-choice-name]');
      if (name) name.textContent = this._choiceName(choice);
      frag.appendChild(node);
    }
    this.choicesEl.appendChild(frag);
  };

  /** Anchor to the projected attachment even when its occupied pin is visually hidden. */
  Placement.prototype._positionPopup = function (slotId) {
    if (!this.popupEl || this.popupEl.hidden) return;
    var point = this.positions[slotId];
    var host = this.pinsEl && this.pinsEl.parentElement;
    var occupied = !!this.placed[slotId];
    if (!host || (!point && !occupied)) return;
    // A visible accessory can be tapped while zoom/pan puts its attachment outside the frame.
    if (point && point.visible === false && !occupied) { this.closeSlot(false); return; }
    if (window.matchMedia('(max-width: 749px)').matches) {
      this.popupEl.style.transform = '';
      this.popupEl.style.left = '';
      this.popupEl.style.top = '';
      return; // The compact sheet stays inside the viewer; CSS owns its position.
    }

    var hostBox = host.getBoundingClientRect();
    var popupBox = this.popupEl.getBoundingClientRect();
    var anchorX = point && Number.isFinite(point.x) ? point.x : hostBox.width / 2;
    var anchorY = point && Number.isFinite(point.y) ? point.y : hostBox.height / 2;
    var gap = 12;
    var x = anchorX + 14 + gap;
    if (x + popupBox.width > hostBox.width) x = anchorX - 14 - popupBox.width - gap;
    x = Math.max(8, Math.min(x, hostBox.width - popupBox.width - 8));
    var y = anchorY - popupBox.height / 2;
    y = Math.max(8, Math.min(y, hostBox.height - popupBox.height - 8));
    this.popupEl.style.transform = 'translate(' + Math.round(x) + 'px,' + Math.round(y) + 'px)';
    this.popupEl.style.left = '0';
    this.popupEl.style.top = '0';
  };

  Placement.prototype.place = function (slotId, choice) {
    // The controller confirms this only after the new scene has loaded.
    this.cancelPlacement(true);
    this.closeSlot();
    this._syncPinStates();
    emit(this.root, 'place', { slotId: slotId, choice: choice });
    return this;
  };

  Placement.prototype.remove = function (slotId) {
    this.closeSlot();
    this.cancelPlacement();
    this._syncPinStates();
    emit(this.root, 'remove', { slotId: slotId });
    return this;
  };

  Placement.prototype.reset = function () {
    var ids = Object.keys(this.placed);
    this.cancelPlacement();
    this.closeSlot();
    this._syncPinStates();
    emit(this.root, 'reset', { slotIds: ids });
    return this;
  };

  var registry = new WeakMap();
  window.BranchPlacement = {
    mount: function (root) {
      if (!root) return null;
      if (registry.has(root)) return registry.get(root);
      var instance = new Placement(root);
      registry.set(root, instance);
      return instance;
    },
    get: function (root) { return registry.get(root) || null; },
  };
})();
